import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCaseRequestSchema,
  DECEASED_RELATIONSHIPS,
  LOSS_TYPES,
  LOSS_TYPE_LABELS,
  type CaseSummary,
  type CreateCaseRequest,
  type DeceasedRelationship,
  type LossType,
} from '@besideu/shared/cases';
import type { UserSummary } from '@besideu/shared/users';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, Select, TextInput } from '@/components/ui/Field.tsx';

export function ManagerNewCase() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const employees = useQuery({
    queryKey: ['manager', 'employees'],
    queryFn: () => api.get<{ employees: UserSummary[] }>('/api/manager/employees'),
  });

  const create = useMutation({
    mutationFn: (input: CreateCaseRequest) => api.post<{ case: CaseSummary }>('/api/cases', input),
    onSuccess: async ({ case: created }) => {
      await queryClient.invalidateQueries({ queryKey: ['cases'] });
      await navigate(`/manager/cases/${created.id}`);
    },
  });

  return (
    <section className="max-w-xl space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">New support pathway</h1>
        <p className="mt-2 text-muted-foreground">
          Log a bereavement so we can schedule touch points and support the right policy steps.
        </p>
      </header>

      {employees.isPending ? (
        <p className="text-muted-foreground">Loading your team…</p>
      ) : employees.error ? (
        <p className="text-traffic-red">Could not load your team.</p>
      ) : employees.data && employees.data.employees.length === 0 ? (
        <NoTeamState />
      ) : (
        <NewCaseForm
          employees={employees.data?.employees ?? []}
          pending={create.isPending}
          submitError={errorMessage(create.error)}
          onSubmit={(input) => create.mutate(input)}
        />
      )}
    </section>
  );
}

function NewCaseForm({
  employees,
  pending,
  submitError,
  onSubmit,
}: {
  employees: UserSummary[];
  pending: boolean;
  submitError: string | null;
  onSubmit: (input: CreateCaseRequest) => void;
}) {
  const firstId = employees[0]?.id ?? '';
  const [employeeId, setEmployeeId] = useState(firstId);
  const [relationship, setRelationship] = useState<DeceasedRelationship>('Parent');
  // '' = not recorded (the field is optional — spec §19 avoids forcing detail).
  const [lossType, setLossType] = useState<LossType | ''>('');
  const [dateOfLoss, setDateOfLoss] = useState(today());
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof CreateCaseRequest, string>>>(
    {},
  );

  function handle(e: FormEvent) {
    e.preventDefault();
    const parsed = CreateCaseRequestSchema.safeParse({
      employeeId,
      deceasedRelationship: relationship,
      // Omit entirely when not recorded so the field stays optional/null.
      ...(lossType ? { lossType } : {}),
      dateOfLoss,
    });
    if (!parsed.success) {
      const next: Partial<Record<keyof CreateCaseRequest, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof CreateCaseRequest;
        if (!next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    onSubmit(parsed.data);
  }

  return (
    <form onSubmit={handle} className="grid gap-5 rounded-lg border border-border p-6">
      <Field label="Employee" error={fieldErrors.employeeId}>
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            required
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Relationship to the deceased"
        error={fieldErrors.deceasedRelationship}
        hint="Used to tailor policy guidance — does not appear in employee-facing chat."
      >
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as DeceasedRelationship)}
          >
            {DECEASED_RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Type of loss (optional)"
        hint="Helps tailor support for specific kinds of loss. Leave as “Not recorded” if you’d rather not say."
      >
        {(id, descProps) => (
          <Select
            id={id}
            {...descProps}
            value={lossType}
            onChange={(e) => setLossType(e.target.value as LossType | '')}
          >
            <option value="">Not recorded</option>
            {LOSS_TYPES.map((t) => (
              <option key={t} value={t}>
                {LOSS_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Date of loss" error={fieldErrors.dateOfLoss}>
        {(id, descProps) => (
          <TextInput
            id={id}
            {...descProps}
            type="date"
            required
            max={today()}
            value={dateOfLoss}
            onChange={(e) => setDateOfLoss(e.target.value)}
          />
        )}
      </Field>

      <div>
        <Button type="submit" loading={pending}>
          Create support pathway
        </Button>
        {submitError ? (
          <p className="mt-2 text-sm text-traffic-red" role="alert">
            {submitError}
          </p>
        ) : null}
      </div>
    </form>
  );
}

function NoTeamState() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/40 p-6">
      <p className="text-muted-foreground">
        You don't have any team members assigned yet. Ask HR to map your reports before opening a
        support pathway.
      </p>
    </div>
  );
}

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'That employee isn’t on your team.';
    if (err.status === 400) return 'Please check the form and try again.';
    return 'Something went wrong creating the support pathway.';
  }
  return 'Network error.';
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
